import { React, useRef, useCallback, memo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCanvas } from '../../../hooks/useCanvas';
import useCanvasStore from '../../../stores/canvasStore';
import { usePublishProject } from '../../../hooks/usePublishProjectAsTemplate.js';
import { useAutosave } from '../../../hooks/useAutosave';
import { useAuth } from '../../../contexts/AuthContext';
import { useExportNotifications } from '../../../hooks/useExportNotifications';
import { getGlobalCreateAudioSprite, getGlobalCreateImageSprite, getGlobalCreateVideoSprite, getGlobalCreateGifSprite, getGlobalCreateAiSprite } from '../../../stores/globalStore';
import ZoomControls from './ZoomControls';
import CustomSelect from '../../UI/CustomSelect';
import CustomInput from '../../UI/CustomInput';
import CustomDropdown from '../../UI/CustomDropdown';
import PublishTemplateModal from '../../UI/PublishTemplateModal';
import ExportProgressModal from './ExportProgressModal';
import ExportSettingsModal from './ExportSettingsModal';
import { ExportNotificationBell } from './ExportNotificationManager'
import { ZoomIn, ZoomOut, Save, LayoutTemplate, Download, Settings, Check, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import logo from '../../../assets/AG-Logo-white.svg';
import dropdownIcon from '../../../assets/dropdown-icon.svg';
import undoIcon from '../../../assets/undo-icon.svg';
import redoIcon from '../../../assets/redo-icon.svg';
import {
  captureCanvasThumbnail,
  captureCanvasThumbnailAtPercentage,
  captureCanvasThumbnailAsBlob,
} from '../../../utils/canvasThumbnailUtils';
import {
  downloadVideo,
  exportCanvasVideoWithPuppeteer as exportPuppeteer,
  getExportFilename,
  getExportResolution,
} from '../../../utils/puppeteerExportUtils';
import { projectsAPI, templatesAPI } from '../../../services/api';
import mockProjects from '../../../data/mockProjects.json';

const CanvasToolbar = () => {
  const {
    setProjectName,
    projectName,
    canvasElements,
    selectElement,
    selectedElement,
    canvasSize,
    canvasBackgroundColor,
    setCanvasSize,
    undo,
    redo,
    canUndo,
    canRedo,
    getHistoryInfo,
    viewport,
    zoomOut,
    zoomIn,
    currentCanvasZoom,
    pixiApp,
    updateSpritesForTime,
    currentTime,
    duration,
    isPlaying,
    isProjectLoading
  } = useCanvas();

  const totalDuration = duration - 3; //Subtract 3 seconds for extra padding at the end

  //Declare Variables
  const MAX_ZOOM = 0.6;
  const MIN_ZOOM = 0.08;

  const { projectId } = useParams();
  const { user } = useAuth();

  const {
    publishProject,
    isPublishing,
    generatePreviewVideo,
    isGeneratingPreview,
    previewProgress,
    previewStage
  } = usePublishProject();

  const { notifications, startPolling, stopPolling, removeNotification } = useExportNotifications();

  // Track last thumbnail generation time to allow periodic updates (every 1 min)
  const lastThumbnailGenerationTimeRef = useRef(0);

  // Export state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStage, setExportStage] = useState('idle'); // 'idle', 'initializing', 'capturing', 'uploading', 'encoding', 'complete', 'error'
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [exportError, setExportError] = useState(null);
  const [exportDownloadUrl, setExportDownloadUrl] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [pollingHandedOff, setPollingHandedOff] = useState(false); // Track if we've handed off to notifications

  // Export method selection (persisted in localStorage)
  const [exportMethod, setExportMethod] = useState(() => {
    const saved = localStorage.getItem('exportMethod');
    return saved || 'ffmpeg';
  });

  // State for publish template modal
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [existingTemplateMetadata, setExistingTemplateMetadata] = useState(null);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  //Get Project Name
  const getProjectName = async () => {
    const getProject = await projectsAPI.getProject(projectId)
    const project = getProject.data.project
    console.log("Project Name:", project.name)
    return project.name;
  }

  //Handle Project Name Change
  const handleChangeProjectName = async (value) => {
    setProjectName(value);
    //A function to send to server to update project name
    const ProjectName = await projectsAPI.changeProjectName(projectId, value)
    return ProjectName;
  }
  // Handle undo - use all global sprite creators
  const handleUndo = () => {
    if (canUndo()) {
      const createVideoSprite = getGlobalCreateVideoSprite();
      const createAudioSprite = getGlobalCreateAudioSprite();
      const createImageSprite = getGlobalCreateImageSprite();
      const createGifSprite = getGlobalCreateGifSprite();
      const createAiSprite = getGlobalCreateAiSprite();

      if (createVideoSprite) {
        console.log('⏪ Undoing from toolbar');
        // Pass all sprite creators as an object
        undo({
          createVideoSprite,
          createAudioSprite,
          createImageSprite,
          createGifSprite,
          createAiSprite
        });
      } else {
        console.log('⏪ Sprite creators not available yet - use Ctrl+Z');
        alert('Please use Ctrl+Z for undo functionality');
      }
    }
  };

  // Handle redo - use all global sprite creators
  const handleRedo = () => {
    if (canRedo()) {
      const createVideoSprite = getGlobalCreateVideoSprite();
      const createAudioSprite = getGlobalCreateAudioSprite();
      const createImageSprite = getGlobalCreateImageSprite();
      const createGifSprite = getGlobalCreateGifSprite();
      const createAiSprite = getGlobalCreateAiSprite();

      if (createVideoSprite) {
        console.log('⏩ Redoing from toolbar');
        // Pass all sprite creators as an object
        redo({
          createVideoSprite,
          createAudioSprite,
          createImageSprite,
          createGifSprite,
          createAiSprite
        });
      } else {
        console.log('⏩ Sprite creators not available yet - use Ctrl+Y');
        alert('Please use Ctrl+Y (or Ctrl+Shift+Z) for redo functionality');
      }
    }
  };

  const canvasPresets = [
    { name: 'Youtube Post (1920×1080)', width: 1920, height: 1080 },
    { name: 'Square (1080×1080)', width: 1080, height: 1080 },
    { name: 'Portrait (1080×1920)', width: 1080, height: 1920 },
    { name: 'Youtube 4K (3840×2160)', width: 3840, height: 2160 },
    { name: 'Custom', width: 0, height: 0 },
  ];

  // Helper function to get canvas name from dimensions
  const getCanvasNameFromDimensions = (width, height) => {
    const preset = canvasPresets.find(
      preset => preset.width === width && preset.height === height
    );
    return preset ? preset.name : `Custom (${width}×${height})`;
  };

  // Check if project has existing template metadata
  useEffect(() => {
    const checkExistingTemplate = async () => {
      if (isAdmin && projectId) {
        try {
          const response = await templatesAPI.getByProjectId(projectId);
          if (response.data.data.template) {
            setExistingTemplateMetadata(response.data.data.template);
          }
        } catch (error) {
          // No existing template, that's fine
          console.log('No existing template found');
        }
      }
    };
    checkExistingTemplate();
  }, [isAdmin, projectId]);

  // Project dropdown items with icons - conditionally include Publish Template for admins
  const projectDropdownItems = [
    {
      label: 'Save Project',
      icon: <Save size={16} />,
      action: 'save'
    },
    ...(isAdmin ? [{
      label: 'Publish Template',
      icon: <LayoutTemplate size={16} />,
      action: 'PublishTemplate'
    }] : []),
  ];

  // Export resolution dropdown items
  const exportDropdownItems = [
    { label: '720p', action: '720p' },
    { label: '1080p', action: '1080p' },
    { label: '4K', action: '4K' },
  ];

  // Handle export method change
  const handleExportMethodChange = (method) => {
    setExportMethod(method);
    localStorage.setItem('exportMethod', method);
    console.log(`[Export] Method changed to: ${method.toUpperCase()}`);
  };

  // Handle export action
  const handleExportVideo = async (format = 'mp4') => {
    setIsExportModalOpen(true);
    setExportStage('preparing');
    setExportProgress(0);
    setExportError(null);
    setExportDownloadUrl(null);
    setCurrentJobId(null);
    setPollingHandedOff(false); // Reset handoff flag

    try {
      const { width: exportWidth, height: exportHeight } = getExportResolution(canvasSize);

      const downloadUrl = await exportPuppeteer(
        {
          canvasSize,
          canvasElements,
          backgroundColor: canvasBackgroundColor || '#000000',
          duration: totalDuration,
          projectId,
          projectName: projectName || 'Untitled',
          fps: format === 'gif' ? 12 : 25,
          exportWidth,
          exportHeight,
          format,
        },
        (stage, _current, _total, percentage, message, jobId) => {
          setExportStage(stage);
          setExportProgress(percentage);
          setExportMessage(message);

          // Track jobId when received and start notification polling ONCE
          if (jobId && !currentJobId) {
            setCurrentJobId(jobId);
            console.log(`[Export] Starting notification polling for job: ${jobId}`);
            startPolling(jobId, projectName || 'Untitled', format);
            setPollingHandedOff(true); // Mark that we've handed off
          }

          console.log(`[Export Progress] ${stage}: ${percentage.toFixed(1)}% - ${message}`);
        }
      );

      setExportDownloadUrl(downloadUrl);
      setExportStage('complete');
    } catch (error) {
      console.error('[Export] Failed:');
      setExportStage('error');
      setExportError('Export failed');
    }
  };

  // Handle download after export complete
  const handleDownloadExportedVideo = (downloadUrl, format = 'mp4') => {
    if (downloadUrl) {
      const filename = getExportFilename(projectName || 'Untitled', format);
      downloadVideo(downloadUrl, filename);
    }
  };

  // Handle cancel export
  const handleCancelExport = async () => {
    if (currentJobId) {
      try {
        // console.log('[Export] Cancelling job:', currentJobId);
        await projectsAPI.cancelPuppeteerExport({ jobId: currentJobId });
        console.log('[Export] Job cancelled successfully');

        // Stop polling for this job
        stopPolling(currentJobId);

        // Remove from notifications
        removeNotification(currentJobId);
      } catch (error) {
        console.error('[Export] Failed to cancel job:', error);
      }
    }

    // Always close modal and reset state
    setIsExportModalOpen(false);
    setTimeout(() => {
      setExportStage('idle');
      setExportProgress(0);
      setExportError(null);
      setExportDownloadUrl(null);
      setCurrentJobId(null);
    }, 300);
  };

  // Close export modal
  const handleCloseExportModal = () => {
    if (!['downloading', 'rendering', 'encoding'].includes(exportStage)) {
      setIsExportModalOpen(false);
      setTimeout(() => {
        setExportStage('idle');
        setExportProgress(0);
        setExportError(null);
        setExportDownloadUrl(null);
        setCurrentJobId(null);
        setPollingHandedOff(false);
      }, 300);
    }
  };

  // Sync modal state with notifications when modal is open
  // This provides seamless continuation after initial export function finishes
  useEffect(() => {
    if (isExportModalOpen && currentJobId && pollingHandedOff) {
      // Find the notification for this job
      const notification = notifications.find(n => n.jobId === currentJobId);

      if (notification) {
        // Map notification status to modal stages
        // Skip 'processing' stage - it's a generic fallback that shouldn't override specific stages
        const stageMap = {
          'processing': null, // Don't override with generic 'processing'
          'complete': 'complete',
          'error': 'error'
        };

        const mappedStatus = stageMap[notification.status];
        // Use the mapped status, or if it's null (processing), use the notification's specific stage
        const newStage = mappedStatus !== null ? mappedStatus : notification.stage;
        const newProgress = notification.progress || 0;

        // Only update if:
        // 1. We have a valid specific stage (not undefined and not generic 'processing')
        // 2. Progress is higher OR it's a terminal status
        const hasValidStage = newStage && newStage !== 'processing';
        const isTerminalStatus = notification.status === 'complete' || notification.status === 'error';

        if (hasValidStage || isTerminalStatus) {
          // Only update if notification has more recent data
          if (newProgress >= exportProgress || isTerminalStatus) {
            // Map backend stages to frontend stages
            const stageMapping = {
              'downloading_media': 'downloading',
              'rendering': 'rendering',
              'encoding': 'encoding',
              'complete': 'complete',
              'error': 'error'
            };
            const frontendStage = stageMapping[newStage] || newStage;

            setExportStage(frontendStage);
            setExportProgress(newProgress);

            // Build a more detailed message
            let message = '';
            if (notification.stage) {
              message = `${notification.stage}... ${Math.round(newProgress)}%`;
            }
            setExportMessage(message);

            if (notification.status === 'complete' && notification.downloadUrl) {
              setExportDownloadUrl(notification.downloadUrl);
            }

            if (notification.status === 'error' && notification.error) {
              setExportError(notification.error);
            }
          }
        }
      }
    }
  }, [notifications, isExportModalOpen, currentJobId, pollingHandedOff, exportProgress]);

  // Extract save logic into a separate function for reuse
  const saveProject = useCallback(async (forceGenerateThumbnail = false) => {
    // Don't save if project is still loading
    if (isProjectLoading) {
      console.log('⏸️ Save blocked - project is still loading');
      return;
    }

    // Don't save if project load had failures - prevents overwriting good layers with incomplete state
    const storeState = useCanvasStore.getState();
    if (storeState.projectLoadFailed) {
      console.warn('🛡️ Save blocked - project load had failures, preventing layer data loss');
      return;
    }

    // Don't save if we have fewer layers than expected (layers may have failed to load)
    if (storeState.expectedLayerCount > 0 && canvasElements.length < storeState.expectedLayerCount) {
      console.warn(`🛡️ Save blocked - canvas has ${canvasElements.length} layers but project expects ${storeState.expectedLayerCount}. Preventing potential layer data loss.`);
      return;
    }

    console.log('Saving project...');
    try {
      // Create project data WITHOUT thumbnail - save data first for speed
      const projectData = {
        id: projectId,
        name: projectName,
        canvas: {
          width: canvasSize.width,
          height: canvasSize.height,
          name: getCanvasNameFromDimensions(canvasSize.width, canvasSize.height),
          backgroundColor: canvasBackgroundColor || '#000000',
        },
        duration: totalDuration,
        layers: canvasElements.map(element => {
          // Remove pixiSprite and captionSprite to prevent circular JSON errors during upload
          const { pixiSprite, captionSprite, ...elementData } = element;
          return elementData;
        })
      };

      // Save project metadata FIRST (prioritize data persistence)
      try {
        const projectResponse = await projectsAPI.saveProject(projectData);
        // console.log('Project saved successfully:', projectResponse.data);
      } catch (saveError) {
        console.error('Error with server operations:', saveError);

        // Check if it's an authentication error
        if (saveError.response?.status === 401) {
          alert('Please log in to save projects');
          return;
        }

        throw saveError; // Re-throw to be caught by autosave
      }

      // Generate and upload thumbnail IN THE BACKGROUND (non-blocking)
      // This runs after the project data is already safely saved
      const shouldGenerateThumbnail = forceGenerateThumbnail || !isPlaying;

      if (shouldGenerateThumbnail) {
        // Fire-and-forget: thumbnail generation + upload happens asynchronously
        (async () => {
          try {
            console.log('Generating thumbnail in background...');
            const thumbnailBlob = await captureCanvasThumbnailAsBlob({
              viewport,
              canvasSize,
              pixiApp,
              updateSpritesForTime,
              totalDuration,
              currentTime,
              captureTime: currentTime,
              width: 640,
              height: 360,
              format: 'image/jpeg',
              quality: 0.1
            });

            const thumbnailFilename = `${projectId}_thumbnail.jpg`;
            const uploadResponse = await projectsAPI.uploadThumbnail(
              thumbnailBlob,
              projectId,
              thumbnailFilename
            );

            const thumbnailUrl = uploadResponse.data.data.thumbnailUrl;
            const thumbnailKey = uploadResponse.data.data.thumbnailKey;
            const filename = uploadResponse.data.data.filename;

            // Update timestamp
            lastThumbnailGenerationTimeRef.current = Date.now();

            // Save thumbnail metadata to project in a lightweight follow-up call
            await projectsAPI.saveProject({
              id: projectId,
              thumbnailUrl,
              thumbnailKey,
              thumbnailFilename: filename,
            });
            console.log('Thumbnail saved in background successfully');
          } catch (thumbError) {
            // Thumbnail failure is non-fatal - project data is already saved
            console.warn('Background thumbnail generation/upload failed (non-fatal):', thumbError);
          }
        })();
      } else {
        console.log(`Skipping thumbnail generation (playing: ${isPlaying})`);
      }

    } catch (error) {
      console.error('Error saving project:', error);
      throw error; // Re-throw for autosave to handle
    }
  }, [projectId, viewport, canvasSize, canvasBackgroundColor, pixiApp, updateSpritesForTime, totalDuration, currentTime, projectName, canvasElements, isPlaying]);

  // Use autosave hook - but don't destructure to avoid rerenders
  useAutosave(canvasElements, saveProject, 2000);

  const handleProjectAction = async (action) => {
    switch (action) {
      case 'save':
        try {
          // Manual save should always generate a fresh thumbnail
          await saveProject(true);
        } catch (error) {
          alert(`Failed to save project: ${error.message}`);
        }
        break;

      case 'PublishTemplate':
        // Open the publish template modal
        setIsPublishModalOpen(true);
        break;

      default:
        console.log('Unknown action:', action);
    }
  };

  // Handle publish template with metadata
  const handlePublishTemplate = async (metadata) => {
    try {
      // Save project first
      await saveProject(true);

      // Get the project
      const getProject = await projectsAPI.getProject(projectId);
      const project = getProject.data.project;

      // Publish with metadata (without preview first)
      const publishResponse = await publishProject(project, {
        description: metadata.description,
        category: metadata.category,
        tags: metadata.tags
      });

      const template = publishResponse.data.template;
      console.log('[Publish] Template published:', template._id);

      // Always generate preview video
      if (template._id) {
        console.log('[Publish] Generating preview video for template:', template._id);

        try {
          await generatePreviewVideo(template._id, (stage, progress, message) => {
            console.log(`[Preview Progress] ${stage}: ${progress}% - ${message}`);
          });

          console.log('[Publish] Preview video generated successfully');
        } catch (previewError) {
          console.error('[Publish] Preview video generation failed:', previewError);
          // Don't fail the entire publish if preview fails
          alert(`Template published, but preview video generation failed: ${previewError.message}`);
        }
      }

      // Close modal and show success
      setIsPublishModalOpen(false);
      alert('Template published with preview video successfully!');

      // Refresh template metadata
      const response = await templatesAPI.getByProjectId(projectId);
      if (response.data.data.template) {
        setExistingTemplateMetadata(response.data.data.template);
      }
    } catch (error) {
      console.error('Error publishing template:', error);
      alert(`Failed to publish template: ${error.response?.data?.message || error.message}`);
    }
  };

  /**
   * Helper function to capture thumbnail at specific time
   * You can easily change the capture time here
   */
  const captureProjectThumbnail = async (captureTimeInSeconds = null) => {
    // Default to middle of project if no time specified
    const captureTime = captureTimeInSeconds !== null
      ? captureTimeInSeconds
      : totalDuration / 2;

    try {
      const thumbnail = await captureCanvasThumbnail({
        viewport,
        pixiApp,
        canvasElements,
        updateSpritesForTime,
        totalDuration,
        currentTime,
        canvasSize,
        captureTime,
        width: 640,
        height: 360,
        format: 'image/jpeg',
        quality: 0.1
      });

      return thumbnail;
    } catch (error) {
      console.error('Error capturing thumbnail:', error);
      return null;
    }
  };

  const handleCanvasSizeChange = (presetName, presetObject) => {
    if (presetName === 'Custom') {
      // For now, just use a simple prompt. In a real app, you'd use a modal
      const width = parseInt(prompt('Enter canvas width:', canvasSize.width));
      const height = parseInt(prompt('Enter canvas height:', canvasSize.height));
      if (width > 0 && height > 0) {
        console.log('Setting custom canvas size:', { width, height });
        setCanvasSize({
          width,
          height,
          name: `Custom (${width}×${height})`
        });
      }
    } else {
      console.log('Setting preset canvas size:', presetObject);
      setCanvasSize({
        width: presetObject.width,
        height: presetObject.height,
        name: presetObject.name
      });
    }
  };

  const currentPreset = canvasPresets.find(
    preset => preset.width === canvasSize.width && preset.height === canvasSize.height
  );

  const handleZoomIn = () => {
    zoomIn(viewport);
    console.log('Zoom in clicked - new zoom:', currentCanvasZoom);
  }

  const handleZoomOut = () => {
    zoomOut(viewport);
    console.log('Zoom out clicked - new zoom:', currentCanvasZoom);
  }
  // Check if zoom buttons should be disabled
  const isZoomInDisabled = currentCanvasZoom >= MAX_ZOOM;
  const isZoomOutDisabled = currentCanvasZoom <= MIN_ZOOM;

  return (
    <>
      <div className="bg-ag-black-700 border-b border-ag-black-400 px-4 py-4 flex items-center justify-between">
        {/* Left Section - File Operations */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center rounded-xl px-2 py-0 space-x-2 border border-ag-black-400">
            <button className="px-2 py-2 text-sm rounded hover:bg-ag-black-800 transition-colors">
              <img src={logo} alt="AG Logo" className="h-7 w-auto" />
            </button>
            <CustomInput
              defaultValue={projectName || "Untitled Project"}
              placeholder="Project Name"
              className="bg-ag-black-800 border border-ag-black-400 rounded-xl font-bold text-base   text-white
                     text-center placeholder-gray-400 focus:outline-none focus:ring-1
                     focus:ring-ag-green w-40 px-2 py-1"
              onUnfocus={(value) => handleChangeProjectName(value)}
            />
            <CustomDropdown
              trigger={
                <button className="px-3 py-3 text-sm rounded-lg bg-ag-black-800 border border-ag-black-400 hover:bg-ag-green transition-colors">
                  <img src={dropdownIcon} alt="dropdown Logo" className="h-2 w-auto hover:invert" />
                </button>
              }
              items={projectDropdownItems}
              onItemClick={handleProjectAction}
              position="bottom-left"
            />
          </div>

          {/* Autosave Status Indicator - Memoized to prevent parent rerenders */}
          <AutosaveStatus canvasElements={canvasElements} saveProject={saveProject} />
        </div>

        {/* Center Section - Canvas Info & Size Selector */}
        <div className="flex items-center space-x-4 text-sm text-white -ml-[184px]">
          {/* Undo/Redo buttons */}
          <div className="flex items-center space-x-1 border border-ag-black-400 rounded-xl px-1">
            <button
              className={`p-2 rounded-lg transition-colors ${canUndo()
                ? 'hover:bg-ag-black-800 text-white'
                : 'text-gray-600 cursor-not-allowed'
                }`}
              onClick={handleUndo}
              disabled={!canUndo()}
              title="Undo (Ctrl+Z)"
            >
              <img
                src={undoIcon}
                alt="Undo"
                className={`h-4 w-4 ${canUndo() ? '' : 'opacity-50'}`}
                style={{ filter: canUndo() ? 'invert(1)' : 'invert(0.4)' }}
              />
            </button>
            <button
              className={`p-2 rounded-lg transition-colors ${canRedo()
                ? 'hover:bg-ag-black-800 text-white'
                : 'text-gray-600 cursor-not-allowed'
                }`}
              onClick={handleRedo}
              disabled={!canRedo()}
              title="Redo (Ctrl+Y)"
            >
              <img
                src={redoIcon}
                alt="Redo"
                className={`h-4 w-4 ${canRedo() ? '' : 'opacity-50'}`}
                style={{ filter: canRedo() ? 'invert(1)' : 'invert(0.4)' }}
              />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <span>Canvas:</span>
            <CustomSelect
              options={canvasPresets}
              value={currentPreset?.name || 'Custom'}
              onChange={handleCanvasSizeChange}
              placeholder="Select canvas size..."
              className="min-w-[140px]"
              displayKey="name"
              valueKey="name"
            />
          </div>
          <div className="flex items-center space-x-1 border border-ag-black-400 rounded-xl px-1">
            {/* Zoom Controls */}
            <button
              onClick={handleZoomOut}
              disabled={isZoomOutDisabled}
              className={`rounded-lg transition-colors ${isZoomOutDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-ag-black-800'
                }`}
              title="Zoom Out"
            >
              <ZoomOut size={34} className="p-2" />
            </button>

            <button
              onClick={handleZoomIn}
              disabled={isZoomInDisabled}
              className={`rounded-lg transition-colors ${isZoomInDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-ag-black-800'
                }`}
              title="Zoom In"
            >
              <ZoomIn size={34} className="p-2" />
            </button>

          </div>
        </div>

        {/* Right Section - Export & Notifications */}
        <div className="flex items-center space-x-3">
          {/* Export Notification Bell */}
          <ExportNotificationBell
            notifications={notifications}
            onDownload={handleDownloadExportedVideo}
            onRemove={removeNotification}
            projectName={projectName}
          />

          {/* Export Button */}
          <CustomDropdown
            trigger={
              <button className="bg-ag-green hover:bg-green-500 text-black font-bold px-6 py-2 rounded-xl flex items-center space-x-2 transition-colors">
                <Download size={18} />
                <span>Export</span>
                <ChevronDown size={16} />
              </button>
            }
            items={[
              { label: 'MP4', action: 'mp4' },
              { label: 'GIF', action: 'gif' },
            ]}
            onItemClick={(action) => handleExportVideo(action)}
            position="bottom-right"
          />
        </div>
        {/* Publish Template Modal */}
        <PublishTemplateModal
          isOpen={isPublishModalOpen}
          onClose={() => !isPublishing && !isGeneratingPreview && setIsPublishModalOpen(false)}
          onPublish={handlePublishTemplate}
          isPublishing={isPublishing || isGeneratingPreview}
          existingMetadata={existingTemplateMetadata}
          isGeneratingPreview={isGeneratingPreview}
          previewProgress={previewProgress}
          previewStage={previewStage}
        />
      </div>

      {/* Export Progress Modal */}
      <ExportProgressModal
        isOpen={isExportModalOpen}
        stage={exportStage}
        progress={exportProgress}
        message={exportMessage}
        error={exportError}
        downloadUrl={exportDownloadUrl}
        onClose={handleCloseExportModal}
        onDownload={handleDownloadExportedVideo}
        onCancel={handleCancelExport}
      />
    </>
  );
};

// Memoized component to prevent parent rerenders when autosave status changes
const AutosaveStatus = memo(({ canvasElements, saveProject }) => {
  const { isSaving, lastSaved, saveStatus } = useAutosave(canvasElements, saveProject, 2000);

  // Format last saved time
  const formatLastSaved = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // difference in seconds

    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center">
      {saveStatus === 'saving' && (
        <>
          <Loader2 size={16} className="text-ag-green animate-spin" />
          <span className="text-sm text-ag-green ml-1">Saving...</span>
        </>
      )}
      {saveStatus === 'saved' && (
        <>
          <Check size={16} className="text-ag-green" />
          <span className="text-sm text-ag-green ml-1">Saved {formatLastSaved(lastSaved)}</span>
        </>
      )}
      {saveStatus === 'error' && (
        <>
          <AlertCircle size={16} className="text-red-400" />
          <span className="text-sm text-red-400 ml-1">Not Saved</span>
        </>
      )}
      {saveStatus === 'idle' && lastSaved && (
        <>
          <Check size={16} className="text-ag-black-300" />
          <span className="text-sm text-ag-black-300 ml-1">Saved {formatLastSaved(lastSaved)}</span>
        </>
      )}
      {saveStatus === 'idle' && !lastSaved && (
        <>
          <Save size={16} className="text-gray-500" />
          <span className="text-sm text-gray-500 ml-1">Autosave enabled</span>
        </>
      )}
    </div>
  );
});

AutosaveStatus.displayName = 'AutosaveStatus';

export default CanvasToolbar;
